import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/api";
import { useI18n } from "@/i18n";
import AdminNav from "@/components/AdminNav";

const mono = "font-mono2 text-[10px] uppercase tracking-wider";
const inputCls = "rounded-md border border-[#1F293D] bg-[#0B0E14] px-3 py-2 text-sm text-slate-200 focus:border-[#00FF87] focus:outline-none";

export default function AdminEmail() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [cfg, setCfg] = useState(null);
  const [logs, setLogs] = useState([]);
  const [templates, setTemplates] = useState(["test"]);
  const [to, setTo] = useState("");
  const [template, setTemplate] = useState("test");
  const [lang, setLang] = useState("fr");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const load = () => {
    api.get("/admin/email/config").then((r) => setCfg(r.data)).catch(() => navigate("/admin/login"));
    api.get("/admin/email/logs", { params: { limit: 30 } }).then((r) => { setLogs(r.data.logs); setTemplates(r.data.templates); }).catch(() => {});
  };
  useEffect(load, []); // eslint-disable-line react-hooks/exhaustive-deps

  const send = async () => {
    setBusy(true);
    setResult(null);
    try {
      const r = await api.post("/admin/email/test", { to, template, lang });
      setResult(r.data);
    } catch (e) {
      setResult({ ok: false, error: formatApiError(e) });
    } finally {
      setBusy(false);
      load();
    }
  };

  return (
    <div className="pt-6" data-testid="admin-email-page">
      <AdminNav />
      <h1 className="font-display text-3xl font-black uppercase tracking-tight text-slate-50">{t("admin.email_title")}</h1>

      {cfg && (
        <div className="mt-4 grid gap-2 rounded-xl border border-[#1F293D] p-4 sm:grid-cols-4" data-testid="email-config">
          <div><p className={`${mono} text-slate-500`}>Mode</p><p className="font-mono2 text-xs text-slate-200" data-testid="email-mode">{cfg.mode}</p></div>
          <div><p className={`${mono} text-slate-500`}>{t("admin.email_sender")}</p><p className="font-mono2 text-xs text-slate-200" data-testid="email-sender">{cfg.sender_name} &lt;{cfg.sender}&gt;</p></div>
          <div><p className={`${mono} text-slate-500`}>BREVO_API_KEY</p><p className={`font-mono2 text-xs ${cfg.api_key_configured ? "text-[#00FF87]" : "text-red-400"}`} data-testid="email-key-state">{cfg.api_key_configured ? t("admin.email_key_ok") : t("admin.email_key_missing")}</p></div>
          <div><p className={`${mono} text-slate-500`}>{t("admin.email_domain")}</p><p className="font-mono2 text-xs text-slate-200">mail.rookieradar.eu</p></div>
        </div>
      )}

      <section className="mt-6 rounded-xl border border-[#1F293D] p-4" data-testid="email-test-form">
        <h2 className={`${mono} mb-3 text-[#00F0FF]`}>{t("admin.email_test_title")}</h2>
        <div className="flex flex-wrap gap-2">
          <input data-testid="email-test-to" type="email" placeholder="destinataire@exemple.com" className={`${inputCls} min-w-[260px] flex-1`} value={to} onChange={(e) => setTo(e.target.value)} />
          <select data-testid="email-test-template" className={inputCls} value={template} onChange={(e) => setTemplate(e.target.value)}>
            {templates.map((x) => <option key={x} value={x}>{x}</option>)}
          </select>
          <select data-testid="email-test-lang" className={inputCls} value={lang} onChange={(e) => setLang(e.target.value)}>
            <option value="fr">fr</option><option value="en">en</option>
          </select>
          <button data-testid="email-test-send" onClick={send} disabled={busy || !to.includes("@")} className="btn-volt disabled:opacity-40">
            {busy ? t("common.loading") : t("admin.email_test_send")}
          </button>
        </div>
        {result && (
          <div className={`mt-4 rounded-md border p-3 ${result.ok ? "border-[#00FF87]/50" : "border-red-500/50"}`} data-testid="email-test-result">
            <p className={`${mono} mb-2 ${result.ok ? "text-[#00FF87]" : "text-red-400"}`} data-testid="email-test-status">
              {result.status || "error"} · HTTP {result.provider_status_code ?? "—"} · messageId {result.provider_message_id || "—"}
            </p>
            <pre className="overflow-x-auto whitespace-pre-wrap break-all font-mono2 text-[11px] text-slate-300" data-testid="email-test-raw">{JSON.stringify(result, null, 2)}</pre>
          </div>
        )}
      </section>

      <section className="mt-6 rounded-xl border border-[#1F293D] p-4" data-testid="email-logs">
        <h2 className={`${mono} mb-3 text-[#00F0FF]`}>{t("admin.email_logs_title")}</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-left font-mono2 text-[10px] text-slate-400">
            <thead><tr className="border-b border-[#1F293D] uppercase text-slate-500"><th className="py-1.5 pr-3">Date</th><th className="pr-3">To</th><th className="pr-3">Template</th><th className="pr-3">Status</th><th className="pr-3">HTTP</th><th className="pr-3">messageId</th><th>Error</th></tr></thead>
            <tbody>
              {logs.map((l) => (
                <tr key={l.id} className="border-b border-[#1F293D]/60" data-testid="email-log-row">
                  <td className="py-1.5 pr-3 text-slate-500">{(l.created_at || "").slice(0, 16).replace("T", " ")}</td>
                  <td className="pr-3 text-slate-200">{l.recipient}</td>
                  <td className="pr-3">{l.template}</td>
                  <td className={`pr-3 ${l.status === "sent" ? "text-[#00FF87]" : l.status === "failed" ? "text-red-400" : ""}`}>{l.status}</td>
                  <td className="pr-3">{l.provider_status_code ?? "—"}</td>
                  <td className="pr-3 break-all">{l.provider_message_id || "—"}</td>
                  <td className="text-red-400/80">{l.error || ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
